import { useEffect, useState } from "react";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, post, put, del, can } from "@/utils/service";
import { apiMessage } from "@/utils/api";
import { useMemberOptions } from "@/components/crm/members";
import { Panel, Field, input, primary, secondary, danger } from "./shared";
import AppSelect from "@/components/ui/app-select";

interface Team { id: string; name: string; description: string | null; lead_user_id: string | null; members: { id: string; first_name: string; last_name: string }[] }

/** Teams group members; records shared with "my team" are visible to the whole team. */
export default function Teams() {
  const editable = can("teams.manage");
  const members = useMemberOptions();
  const [teams, setTeams] = useState<Team[]>([]);
  const [editing, setEditing] = useState<{ id?: string; name: string; description: string; lead_user_id: string; member_ids: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => get<{ data: { teams: Team[] } }>("/teams").then((r) => setTeams(r.data.teams)).catch((e) => notify(apiMessage(e, "Could not load teams.")));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    const body = { name: editing.name, description: editing.description || null, lead_user_id: editing.lead_user_id || null, member_ids: editing.member_ids };
    try {
      if (editing.id) await put(`/teams/${editing.id}`, body); else await post("/teams", body);
      notify("Team saved");
      setEditing(null);
      load();
    } catch (e) { notify(apiMessage(e, "Could not save the team.")); }
    finally { setBusy(false); }
  };
  const remove = async (t: Team) => {
    if (!await confirmDialog(`Delete the team "${t.name}"? Records shared with this team become visible only to their owners and managers.`)) return;
    try { await del(`/teams/${t.id}`); notify("Team deleted"); load(); }
    catch (e) { notify(apiMessage(e, "Could not delete the team.")); }
  };

  return (
    <Panel title="Teams" description="Group people who work together; share records with a whole team" actions={editable && !editing && <button onClick={() => setEditing({ name: "", description: "", lead_user_id: "", member_ids: [] })} className={secondary}>New team</button>}>
      {editing && (
        <div className="mb-6 space-y-4 rounded-lg border border-gray-200 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Team name"><input className={input} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
            <Field label="Team lead"><AppSelect className={input} value={editing.lead_user_id} onChange={(e) => setEditing({ ...editing, lead_user_id: e.target.value })}><option value="">None</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</AppSelect></Field>
            <div className="md:col-span-2"><Field label="Description"><input className={input} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field></div>
          </div>
          <Field label="Members">
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {members.map((m) => (
                <label key={m.id} className="flex items-center gap-1.5 text-xs text-gray-700">
                  <input type="checkbox" className="accent-primary" checked={editing.member_ids.includes(m.id)} onChange={(e) => setEditing({ ...editing, member_ids: e.target.checked ? [...editing.member_ids, m.id] : editing.member_ids.filter((x) => x !== m.id) })} />
                  {m.name}
                </label>
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2"><button onClick={() => setEditing(null)} className={secondary}>Cancel</button><button onClick={save} disabled={busy || !editing.name} className={primary}>{busy ? "Saving..." : "Save team"}</button></div>
        </div>
      )}
      {teams.length === 0 ? <p className="text-xs text-gray-500">No teams yet.</p> : (
        <div className="divide-y divide-gray-100">
          {teams.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="text-xs font-semibold text-gray-900">{t.name}</p>
                <p className="text-xs text-gray-500">{t.members.map((m) => `${m.first_name} ${m.last_name}`).join(", ") || "No members"}</p>
              </div>
              {editable && <div className="flex gap-2"><button className={secondary} onClick={() => setEditing({ id: t.id, name: t.name, description: t.description ?? "", lead_user_id: t.lead_user_id ?? "", member_ids: t.members.map((m) => m.id) })}>Edit</button><button className={danger} onClick={() => remove(t)}>Delete</button></div>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
